import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetenciesFinderBulkCreateDocument,
  CompetenciesCompetenciesFinderInitialDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  type Competency,
  type CompetencyConfig,
  type SelectedCompetency,
  getNewCompetencyInputs,
  hasToBeCreated,
  selectionToCompanyCompetencies,
  useCompetencyFinder,
} from 'modules/competencies/hooks/useCompetencyFinder'

import { Combobox } from './Combobox'
import { CreateForm } from './CreateForm'
import { Suggestions } from './Suggestions'
import { type CompetencySuggestionTarget } from './Suggestions/useSuggestions'

export type { Competency, CompetencySuggestionTarget }

type Props = {
  target?: CompetencySuggestionTarget
  currentCompetencies: CompetencyConfig[]
  onSubmit: (newCompetencies: Competency[]) => void | Promise<void>
  onClose: () => void
}

export const CompetenciesFinder: React.FC<Props> = ({
  target,
  currentCompetencies = [],
  onSubmit,
  onClose,
}) => {
  const { company } = useCurrentSession()
  const [submitting, setSubmitting] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createFormName, setCreateFormName] = useState('')
  const initialCompetencyIds = currentCompetencies.map((config) => config.id)
  const { data } = useQuery(CompetenciesCompetenciesFinderInitialDocument, {
    variables: { ids: initialCompetencyIds, companyId: company.id },
  })

  const canManage = data?.competencies?._policies?.competenciesManage?.can ?? false
  const isLegacyJobCatalogEnabled =
    data?.features?.featuresConnection?.edges?.find(
      ({ node }) => node.name === 'legacy_job_catalog'
    )?.node?.enabled ?? false
  const isJobCatalogTreeEnabled = !isLegacyJobCatalogEnabled

  const {
    selectedCompetencies,
    initialFactorialUuids,
    excludeUuidSuggestions,
    addCompetency,
    removeCompetency,
    syncCreated,
  } = useCompetencyFinder({
    initialConfig: currentCompetencies,
    fetchedCompetencies: data?.competencies?.competencies || [],
  })

  const [bulkCreate] = useMutation(CompetenciesCompetenciesFinderBulkCreateDocument, {
    extractorFn: (d) => d.competenciesMutations.bulkCreateCompetenciesCompetency,
  })

  const handleUpsert = async (): Promise<Competency[]> => {
    const competencyInputs = getNewCompetencyInputs(selectedCompetencies.filter(hasToBeCreated))

    if (!competencyInputs.length) {
      return selectionToCompanyCompetencies(selectedCompetencies)
    }

    const response = await bulkCreate({
      variables: {
        competencies: competencyInputs,
      },
    })
    const result = handleResult(response, () => {})
    if (result.success) {
      const competencies = syncCreated(result?.data?.competencies || [])
      return selectionToCompanyCompetencies(competencies)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
      return selectionToCompanyCompetencies(selectedCompetencies)
    }
  }
  const handleSubmit = async () => {
    setSubmitting(true)
    const competencies = await handleUpsert()

    try {
      await Promise.resolve(onSubmit(competencies))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateNew = (name = '') => {
    if (canManage) {
      setCreateFormName(name)
      setShowCreateForm(true)
    }
  }

  const handleFormSubmit = (newCompetency: SelectedCompetency) => {
    addCompetency(newCompetency)
    setShowCreateForm(false)
  }

  const handleFormClose = () => {
    setShowCreateForm(false)
  }

  const showFactorialSearches = isJobCatalogTreeEnabled && canManage
  const showFactorialSuggestions = target && isJobCatalogTreeEnabled && canManage

  const title = target?.title
    ? i18n.t('competencies_v3.competencies_finder.title_for', { name: target.title })
    : i18n.t('competencies_v3.competencies_finder.title')
  return (
    <Modal
      illustration="performance"
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose} loading={submitting}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit loading={submitting}>
            {i18n.t('competencies_v3.competencies_finder.assign_button')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Combobox
          useFactorialCompetencies={showFactorialSearches}
          selectedCompetencies={selectedCompetencies}
          canCreate={canManage}
          onAdd={addCompetency}
          onRemove={removeCompetency}
          onCreateNew={handleCreateNew}
        />
        {showCreateForm && (
          <CreateForm name={createFormName} onSubmit={handleFormSubmit} onClose={handleFormClose} />
        )}
        {showFactorialSuggestions && (
          <Suggestions
            target={target}
            initialUuids={initialFactorialUuids}
            excludeUuids={excludeUuidSuggestions}
            onAdd={addCompetency}
          />
        )}
      </Box>
    </Modal>
  )
}
